import { FC, useContext, useState, useEffect, useRef } from "react";
import GlobalContext from "src/context/global-context";
import classNames from "classnames";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import { getErrorMessage, logger } from "src/lib/utils";
import { useMutation } from "react-query";
import { Table, Empty } from "antd";
import type { TablePaginationConfig } from "antd/es/table";
import { DateRangePicker } from "rsuite";
import { DateRange } from "rsuite/esm/DateRangePicker/types";
import useAuth from "src/hooks/use-auth";
import moment from "moment";
import client from "../../api";
import {
  APISomethingWrongMsg,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import PermissionGate from "src/components/shared/permission-gate";

interface ITransaction {
  key: string;
  id: number;
  user_id: number;
  user_type: string;
  asset_id: number;
  reference_id: number;
  receipt_type: string;
  amount: number;
  amount_fee: number;
  total: string;
  from_currency: string;
  to_currency: string;
  rate_used: string;
  converted_amount: string;
  email: string;
  first_name: string;
  last_name: string;
  id_number: string;

  sender_email?: string;
  receiver_email?: string;
  payment_system: string;
  channel_name: string;
  transaction_id: string | null;

  status: string;
  type: string;

  title: string;
  url: string;

  sender_name?: string;
  receiver_name?: string;
  to_amount?: number;
  beneficiary_name?: string;
  beneficiary_bank?: string;
  iban?: string;

  balance_before: string | null;
  balance_after: string | null;

  asset_code: string;

  description: string | null;

  created_at: string;
  updated_at: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const LIST_PAGE_SIZE = 10;

const formatLabel = (value: string) =>
  value?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
interface SelectDropdownProps {
  options: { label: string; value: string }[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

const SelectDropdown: FC<SelectDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={ref} className={classNames("relative", className)}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex items-center justify-between gap-2 h-[42px] min-w-[160px] px-3 py-2 bg-white border border-[#E6E8EC] rounded-[8px] text-[14px] text-primary font-medium shadow-sm hover:border-brand transition-colors"
      >
        <span className="truncate">{selected?.label ?? placeholder}</span>
        <svg
          className={classNames(
            "w-4 h-4 text-secondary flex-shrink-0 transition-transform duration-200",
            isOpen && "rotate-180",
          )}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full min-w-[180px] bg-white border border-[#E6E8EC] rounded-[8px] shadow-lg py-1 max-h-60 overflow-auto">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
              className={classNames(
                "w-full text-left px-3 py-2 text-[14px] hover:bg-gray-50 transition-colors",
                opt.value === value
                  ? "text-brand font-semibold"
                  : "text-primary font-normal",
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Export Buttons ───────────────────────────────────────────────────────────

interface ExportButtonProps {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
}

const ExportButton: FC<ExportButtonProps> = ({
  label,
  icon,
  onClick,
  disabled,
  loading,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled || loading}
    className={classNames(
      "flex items-center gap-2 h-[42px] px-4 rounded-[8px] text-[14px] font-medium border transition-colors",
      disabled || loading
        ? "opacity-50 cursor-not-allowed bg-white border-[#E6E8EC] text-secondary"
        : "bg-white border-[#E6E8EC] text-primary hover:border-brand hover:text-brand shadow-sm",
    )}
  >
    {loading ? (
      <svg
        className="animate-spin w-[18px] h-[18px] text-secondary"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
        />
      </svg>
    ) : (
      icon
    )}
    {label}
  </button>
);

// ─── Icons ────────────────────────────────────────────────────────────────────

const ExcelIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="16" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const PdfIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <path d="M9 15v-2h2a1 1 0 0 1 0 2H9z" />
    <path d="M9 18v-1" />
    <path d="M13 13v5" />
    <path d="M13 13h1.5a1.5 1.5 0 0 1 0 3H13" />
  </svg>
);

const SearchIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="text-secondary"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

// ─── Badge Components ─────────────────────────────────────────────────────────

const statusBadgeConfig: Record<
  string,
  { bg: string; text: string; dot: string }
> = {
  completed: { bg: "bg-green-50", text: "text-green-700", dot: "bg-green-500" },
  pending: {
    bg: "bg-yellow-50",
    text: "text-yellow-700",
    dot: "bg-yellow-500",
  },
  failed: { bg: "bg-red-50", text: "text-red-700", dot: "bg-red-500" },
  cancelled: { bg: "bg-gray-100", text: "text-gray-600", dot: "bg-gray-400" },
};

const StatusBadge: FC<{ status: string }> = ({ status }) => {
  const cfg = statusBadgeConfig[status] ?? {
    bg: "bg-gray-100",
    text: "text-gray-600",
    dot: "bg-gray-400",
  };
  return (
    <span
      className={classNames(
        "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[12px] font-medium capitalize",
        cfg.bg,
        cfg.text,
      )}
    >
      <span
        className={classNames(
          "w-1.5 h-1.5 rounded-full flex-shrink-0",
          cfg.dot,
        )}
      />
      {status}
    </span>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const PaymentsTransactionHistory: FC = () => {
  const { canView, canList, canExport, canDownload } = usePermissions(permissionConstants.TRANSACTION_HISTORY_NEW_INDIVIDUAL);
  const canViewTransactions = canView || canList;
  const canExportTransactions = canExport || canDownload;

  const { setIsAlertOpened, setAlertText, setAlertTitle } =
    useContext(GlobalContext);
  const isMounted = useIsMounted();
  const { isAuthorized } = useAuth();
  const [transactionType, setTransactionType] = useState("all");
  const [status, setStatus] = useState("all");
  const [paymentSystem, setPaymentSystem] = useState("all");
  const [channelName, setChannelName] = useState("all");
  const [currency, setCurrency] = useState("all");
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [currencyOptions, setCurrencyOptions] = useState<
    { label: string; value: string }[]
  >([
    {
      label: "All Currencies",
      value: "all",
    },
  ]);

  const [statusOptions, setStatusOptions] = useState<
    { label: string; value: string }[]
  >([{ label: "All Statuses", value: "all" }]);

  const [paymentSystemOptions, setPaymentSystemOptions] = useState<
    { label: string; value: string }[]
  >([{ label: "All Payment Systems", value: "all" }]);
  const [searchText, setSearchText] = useState("");
  const [dateRangeValue, setDateRangeValue] = useState<DateRange | null>(null);

  // ── Pagination ──
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);

  // ── Data ──
  const [dataSource, setDataSource] = useState<ITransaction[]>([]);

  const now = new Date();
  const prevMonth = new Date(new Date().setMonth(now.getMonth() - 1));
  const { afterToday } = DateRangePicker;
  const [startDate, endDate] = dateRangeValue || [];

  // ── Error handler (shared) ──
  const handleApiError = (error: any) => {
    if (error.code === "ERR_NETWORK") {
      setAlertText(NetworkDisconnectMsg);
    } else {
      if (error.response) {
        setAlertText(getErrorMessage(error.response.data));
      } else {
        setAlertText(APISomethingWrongMsg);
      }
    }
    setIsAlertOpened(true);
  };

  // ── List Mutation ──
  const { mutate: getList, isLoading: isListLoading } = useMutation(
    client.newTransactionsHistory.AllPaymentsHistory,
    {
      onSuccess: (data: any) => {
        logger.log("AllPaymentsHistory response:", data);
        setTotal(data?.totalcount ?? 0);
        setDataSource(
          (data?.items ?? []).map((item: any) => ({
            key: String(item?.id),
            id: item?.id,
            user_id: item?.user_id,
            user_type: item?.user_type,
            reference_id: item.reference_id,
            receipt_type: item.receipt_type,
            asset_id: item?.asset_id,
            amount: Number(item?.amount),
            amount_fee: Number(item?.amount_fee),
            total: item?.total,
            balance_after: item?.balance_after,
            balance_before: item?.balance_before,
            payment_system: item?.payment_system ?? "",
            channel_name: item.channel_name,
            status: item?.status,
            type: item?.type,
            to_amount: item?.to_amount,
            sender_name: item?.sender_name,
            receiver_name: item?.receiver_name,
            description: item?.description ?? "",
            transaction_id: item?.transaction_id ?? "",
            created_at: item?.created_at,
            updated_at: item?.updated_at,
            asset_code: item?.asset_code ?? "",
            to_currency: item?.to_currency ?? "",
            from_currency: item?.from_currency ?? "",
            converted_amount: item?.converted_amount ?? "_",
            rate_used: item?.rate_used ?? "",
            title: item.title,
            url: item.url,
            beneficiary_name: item.beneficiary_name,
            beneficiary_bank: item.beneficiary_bank,
            iban: item.iban,
            email: item.email,
            first_name: item.first_name,
            last_name: item.last_name,
            id_number: item.id_number,

            sender_email: item.sender_email,
            receiver_email: item.receiver_email,
          })),
        );
      },
      onError: handleApiError,
    },
  );

  //Dropdown mutation
  const { mutate: getDropdownsList, isLoading: isDropdownLoading } =
    useMutation(client.newTransactionsHistory.AllPaymentsHistoryDropdowns, {
      onSuccess: (response: any) => {
        const dropdowns = response?.data?.data || response?.data || response;

        setCurrencyOptions([
          {
            label: "All Currencies",
            value: "all",
          },
          ...(dropdowns?.currencies ?? []).map((item: any) => ({
            label: item.code,
            value: String(item.asset_id),
          })),
        ]);
        setStatusOptions([
          { label: "All Statuses", value: "all" },
          ...(dropdowns?.statuses ?? []).map((item: string) => ({
            value: item,
            label: formatLabel(item),
          })),
        ]);

        setPaymentSystemOptions([
          { label: "All Payment Systems", value: "all" },
          ...(dropdowns?.paymentSystems ?? []).map((item: any) => ({
            value: item?.name,
            label: formatLabel(item?.name),
          })),
        ]);
      },
      onError: handleApiError,
    });

  // ── Export Excel Mutation ──
  const { mutate: exportExcel, isLoading: isExcelLoading } = useMutation(
    client.newTransactionsHistory.ExportAllPaymentsHistoryExcel,
    {
      onSuccess: (data: any) => {
        logger.log("ExportAllPaymentsHistoryExcel response:", data);
        setAlertTitle("Success");
        setAlertText(
          data?.message,
        );
        setIsAlertOpened(true);
      },
      onError: handleApiError,
    },
  );

  // ── Export PDF Mutation ──
  const { mutate: exportPdf, isLoading: isPdfLoading } = useMutation(
    client.newTransactionsHistory.ExportAllPaymentsHistoryPdf,
    {
      onSuccess: (data: any) => {
        logger.log("ExportAllPaymentsHistoryPdf response:", data);
        setAlertTitle("Success");
        setAlertText(
          data?.message,
        );
        setIsAlertOpened(true);
      },
      onError: handleApiError,
    },
  );

  const { mutate: downloadReceipt, isLoading: downloadReceiptLoading } =
    useMutation(client.newTransactionsHistory.downloadTransactionReceiptPdf, {
      onSuccess: (pdfBlob, variables) => {
        const blob = new Blob([pdfBlob], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `invoice_${variables.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();

        setAlertText("Receipt downloaded successfully");
        setIsAlertOpened(true);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          setAlertText(
            error.response
              ? getErrorMessage(error.response.data)
              : APISomethingWrongMsg,
          );
        }
        setIsAlertOpened(true);
      },
    });

  const receiptBtn = (record: ITransaction) => {
    if (!canDownload) {
      setAlertText("You do not have permission to download receipt");
      setIsAlertOpened(true);
      return;
    }
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    setDownloadingId(record.id);

    downloadReceipt(
      {
        // receipt_type: record.receipt_type,
        receipt_type: record?.receipt_type,
        id: record?.reference_id,
        timezone,
      },
      {
        onSettled: () => {
          setDownloadingId(null);
        },
      },
    );
  };

  const buildParams = (page: number) => ({
    page,
    perPage: LIST_PAGE_SIZE,
    userType: "individual",
    ...(searchText.length > 0 && {}),
    ...(startDate && { startDate: moment(startDate).format("YYYY-MM-DD") }),
    ...(endDate && { endDate: moment(endDate).format("YYYY-MM-DD") }),
    ...(transactionType !== "all" && { type: transactionType }),
    ...(paymentSystem !== "all" && {
      payment_system: paymentSystem,
    }),
    ...(channelName !== "all" && {
      channel_name: channelName,
    }),
    ...(status !== "all" && { status }),
    ...(currency !== "all" && {
      currency,
    }),
  });

  const buildExportFilters = () => ({
    ...(startDate && {
      from: moment(startDate).format("YYYY-MM-DD"),
    }),

    ...(endDate && {
      to: moment(endDate).format("YYYY-MM-DD"),
    }),

    ...(currency !== "all" && {
      currency,
    }),

    ...(paymentSystem !== "all" && {
      payment_system: paymentSystem,
    }),

    ...(status !== "all" && {
      status,
    }),
    user_type: "individual",
  });

  useEffect(() => {
    if (!isMounted || !isAuthorized || !canViewTransactions) return;

    setCurrentPage(1);

    getList(buildParams(1));
  }, [
    isMounted,
    isAuthorized,
    canViewTransactions,
    transactionType,
    status,
    paymentSystem,
    currency,
    dateRangeValue,
  ]);

  useEffect(() => {
    if (!isMounted || !isAuthorized || !canViewTransactions) return;
    getDropdownsList({ user_type: "individual" });
  }, [isMounted, isAuthorized, canViewTransactions]);

  // Reset to page 1 and refetch when filters change
  const handleSearch = () => {
    setCurrentPage(1);
    getList(buildParams(1));
  };

  const handleExportExcel = () => {
    if (!canExportTransactions) {
      setAlertText("You do not have permission to export transactions");
      setIsAlertOpened(true);
      return;
    }
    exportExcel({
      filters: buildExportFilters(),
    });
  };

  const handleExportPdf = () => {
    if (!canExportTransactions) {
      setAlertText("You do not have permission to export transactions");
      setIsAlertOpened(true);
      return;
    }
    exportPdf({
      filters: buildExportFilters(),
    });
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    const page = pagination.current ?? 1;
    setCurrentPage(page);
    getList(buildParams(page));
  };

  const isExportDisabled = isListLoading || isExcelLoading || isPdfLoading;

  // ── Ant Design Table Columns ──
  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 260,
      render: (val: string) => (
        <span
          className="font-mono text-[11px] text-secondary block max-w-[240px] truncate"
          title={val}
        >
          {val || "—"}
        </span>
      ),
    },
    {
      title: "Trx ID",
      dataIndex: "transaction_id",
      key: "transaction_id",
      width: 260,
      render: (val: string) => (
        <span
          className="font-mono text-[11px] text-secondary block max-w-[240px] truncate"
          title={val}
        >
          {val || "—"}
        </span>
      ),
    },
    {
      title: "User",
      width: 240,
      render: (_: any, record: ITransaction) => (
        <div className="flex flex-col text-[13px]">
          <span className="font-semibold">
            {record.first_name} {record.last_name}
          </span>

          <span className="text-secondary">{record.email}</span>

          <span className="text-secondary">ID : {record.user_id}</span>
        </div>
      ),
    },
    // {
    //   title: "Sender",
    //   width: 220,
    //   render: (_: any, record: ITransaction) => {
    //     if (!record.sender_name && !record.sender_email) {
    //       return "—";
    //     }

    //     return (
    //       <div className="flex flex-col text-[13px]">
    //         <span className="font-medium">{record.sender_name ?? "—"}</span>

    //         <span className="text-secondary">{record.sender_email ?? "—"}</span>

    //         {/* <span className="text-secondary">
    //       ID : {record.user_id}
    //     </span> */}
    //       </div>
    //     );
    //   },
    // },
    // {
    //   title: "Receiver",
    //   width: 220,
    //   render: (_: any, record: ITransaction) => {
    //     if (!record.receiver_name && !record.receiver_email) {
    //       return "—";
    //     }

    //     return (
    //       <div className="flex flex-col text-[13px]">
    //         <span className="font-medium">{record.receiver_name ?? "—"}</span>

    //         <span className="text-secondary">
    //           {record.receiver_email ?? "—"}
    //         </span>
    //       </div>
    //     );
    //   },
    // },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 170,
      render: (val: string, record: ITransaction) => (
        <div className="flex flex-col gap-0.5">
          <span className="text-primary text-[13px] font-medium leading-tight">
            {val || "—"}
          </span>
        </div>
      ),
    },
    {
      title: "Channel name",
      dataIndex: "channel_name",
      key: "channel_name",
      width: 170,
      render: (val: string) => (
        <span className="text-secondary text-[13px] capitalize">
          {val ? val.replace(/_/g, " ") : "—"}
        </span>
      ),
    },
    {
      title: "Payment System",
      dataIndex: "payment_system",
      key: "payment_system",
      width: 170,
      render: (val: string) => (
        <span className="text-secondary text-[13px] capitalize">
          {val ? val.replace(/_/g, " ") : "—"}
        </span>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 110,
      render: (val: string) => val,
    },
    {
      title: (
        <div className="w-full text-center font-manrope text-[14px] font-semibold">
          Currency
        </div>
      ),
      dataIndex: "asset_code",
      key: "asset_code",
      width: 200,
      render: (_: any, record: ITransaction) => {
        if (
          record.payment_system === "conversion" &&
          record.from_currency &&
          record.to_currency
        ) {
          return (
            <div className="flex items-center justify-center h-full w-full min-h-[70px]">
              <div className="flex flex-col items-center gap-1 text-center min-w-[170px]">
                <div className="text-[14px] font-semibold text-primary">
                  {record.from_currency}
                  <span className="mx-2 text-secondary">→</span>
                  {record.to_currency}
                </div>

                <div className="text-[13px] text-secondary">
                  Rate:
                  <span className="ml-1 font-semibold text-primary">
                    {record.rate_used}
                  </span>
                </div>

                <div className="text-[13px] text-secondary">
                  Received:
                  <span className="ml-1 font-semibold text-primary">
                    {record.to_amount} {record.to_currency}
                  </span>
                </div>
              </div>
            </div>
          );
        }

        return (
          <div className="flex items-center justify-center h-full w-full min-h-[70px]">
            <span className="text-[14px] font-semibold uppercase text-primary">
              {record.asset_code || "—"}
            </span>
          </div>
        );
      },
      // render: (val: string, record: ITransaction) => (
      //   <div className="flex flex-col gap-0.5">
      //     <span className="text-primary font-semibold text-[13px] uppercase">
      //       {val || "—"}
      //     </span>
      //   </div>
      // ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 110,
      render: (val: number, record: ITransaction) =>
        // const isCredit = ["deposit", "conversion"].includes(record.type);
        // return (
        // <span
        //   className={classNames(
        //     "font-semibold whitespace-nowrap tabular-nums",
        //     isCredit ? "text-green-600" : "text-red-500",
        //   )}
        // >
        // {isCredit ? "+" : "−"}
        // {
        Number(val).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 8,
        }),
      // }
      //   </span>
      // );
    },
    {
      title: "Fee",
      dataIndex: "amount_fee",
      key: "amount_fee",
      width: 90,
      render: (val: number) => (
        <span className="text-secondary text-[13px] tabular-nums">
          {Number(val).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 8,
          })}
        </span>
      ),
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      width: 110,
      render: (val: string) => (
        <span className="text-secondary text-[13px] tabular-nums">
          {val ?? "—"}
        </span>
      ),
    },
    {
      title: "Balance Before",
      dataIndex: "balance_before",
      key: "balance_before",
      width: 110,
      render: (val: string) => (
        <span className="text-secondary text-[13px] tabular-nums">
          {val ?? "—"}
        </span>
      ),
    },
    {
      title: "Balance after",
      dataIndex: "balance_after",
      key: "balance_after",
      width: 110,
      render: (val: string) => (
        <span className="text-secondary text-[13px] tabular-nums">
          {val ?? "—"}
        </span>
      ),
    },
    {
      title: "Date & Time",
      dataIndex: "created_at",
      key: "created_at",
      width: 150,
      render: (val: string) => (
        <span className="text-secondary text-[13px] whitespace-nowrap">
          {val ? moment(val).format("YYYY-MM-DD HH:mm:ss") : "—"}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (val: string) => <StatusBadge status={val} />,
    },
    {
      title: "Receipt",
      key: "receipt",
      render: (_: any, record: ITransaction) =>
        record.status !== "completed" || !canDownload ? (
          "—"
        ) : (
          <button
            type="button"
            disabled={downloadingId === record.id}
            onClick={() => receiptBtn(record)}
            className="text-secondary underline hover:text-primary disabled:text-gray-400"
          >
            {downloadingId === record.id ? "Downloading..." : "Receipt"}
          </button>
        ),
    },
  ];

  if (!canViewTransactions) {
    return (
      <div className="w-full h-full bg-gray p-6">
        <div className="bg-white rounded-lg p-6 text-center text-secondary">
          You don't have permission to view transactions history.
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-gray">
      <div className="w-full h-full flex flex-col py-2 px-4 sm:p-4 sm:pr-11 gap-4">
        {/* ── Page Title ── */}
        <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium leading-[37.5px] sm:leading-[48px]">
          Payment Transactions
        </p>

        {/* ── Filter Bar ── */}
        <div className="flex flex-wrap items-center gap-3 justify-between">
          {/* Left: dropdowns + search */}
          <div className="flex flex-wrap items-center gap-2">
            <SelectDropdown
              options={statusOptions}
              value={status}
              onChange={(v) => setStatus(v)}
            />

            <SelectDropdown
              options={paymentSystemOptions}
              value={paymentSystem}
              onChange={(v) => setPaymentSystem(v)}
            />

            <SelectDropdown
              options={currencyOptions}
              value={currency}
              onChange={(v) => setCurrency(v)}
            />
          </div>

          {/* Right: date range + export */}
          <div className="flex items-center gap-2 flex-wrap">
            <DateRangePicker
              format="dd/MM/yyyy"
              size="lg"
              block
              placement="bottomEnd"
              style={{ width: "308px" }}
              value={dateRangeValue}
              onChange={setDateRangeValue}
              defaultCalendarValue={[prevMonth, now]}
              shouldDisableDate={afterToday()}
              ranges={[]}
            />

            {/* <PermissionGate
              module={[permissionConstants.TRANSACTION_HISTORY_NEW_INDIVIDUAL]}
              action="can_export"
            >
              <ExportButton
                label="Excel"
                icon={<ExcelIcon />}
                onClick={handleExportExcel}
                disabled={isExportDisabled}
                loading={isExcelLoading}
              />
            </PermissionGate>
            <PermissionGate
              module={[permissionConstants.TRANSACTION_HISTORY_NEW_INDIVIDUAL]}
              action="can_export"
            >
              <ExportButton
                label="PDF"
                icon={<PdfIcon />}
                onClick={handleExportPdf}
                disabled={isExportDisabled}
                loading={isPdfLoading}
              />
            </PermissionGate> */}
          </div>
        </div>

        {/* ── Table Card ── */}
        <div className="flex flex-col mt-1 bg-white rounded-[12px] shadow-sm border border-[#E6E8EC] overflow-hidden">
          {/* Result count + clear filters */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-[#F0F2F5]">
            <span className="text-[13px] text-secondary font-medium">
              {total} transaction{total !== 1 ? "s" : ""}
            </span>
            {(transactionType !== "all" ||
              status !== "all" ||
              searchText ||
              paymentSystem !== "all" ||
              channelName !== "all" ||
              dateRangeValue) && (
                <button
                  type="button"
                  onClick={() => {
                    setTransactionType("all");
                    setStatus("all");
                    setSearchText("");
                    setDateRangeValue(null);
                    setPaymentSystem("all");
                    setChannelName("all");
                    setCurrentPage(1);
                    // Refetch with cleared filters
                    setTimeout(() => {
                      getList({
                        page: 1,
                        perPage: LIST_PAGE_SIZE,
                      });
                    }, 0);
                  }}
                  className="text-[13px] text-brand font-medium hover:underline"
                >
                  Clear filters
                </button>
              )}
          </div>
          <div className="custom-scroll bg-white rounded-r-[12px] rounded-bl-[12px] w-full relative">
            {/* Ant Design Table */}
            <div className="overflow-x-auto">
              <Table<ITransaction>
                dataSource={dataSource}
                columns={columns}
                loading={isListLoading}
                rowKey="key"
                scroll={{ x: 900 }}
                locale={{
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-[15px] text-secondary font-medium">
                            No transactions found
                          </span>
                          {/* <span className="text-[13px] text-secondary/60">
                          Try adjusting your filters or date range
                        </span> */}
                        </div>
                      }
                    />
                  ),
                }}
                pagination={{
                  current: currentPage,
                  pageSize: LIST_PAGE_SIZE,
                  total,
                  showSizeChanger: false,
                  position: ["bottomCenter"],

                  showTotal: (tot, range) =>
                    `${range[0]}–${range[1]} of ${tot} transactions`,
                  className: "px-5 py-3",
                }}
                onChange={handleTableChange}
                className="payments-transaction-table"
                rowClassName="hover:bg-[#FAFBFC] transition-colors"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentsTransactionHistory;
